// The react-app-polyfill imports MUST be the first imports in the file
import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';

import 'date-time-format-timezone';
import 'intl-pluralrules';
import 'typeface-roboto';

import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';

import {
  authFactory,
  AuthProvider,
  AuthProviderName,
  checkitTheme,
  hasAuthGatewayOidcTokens,
  hasCognitoTokens,
  MicroApp,
  ModuleName,
  UserRoleName
} from '@checkit/react-components';
import { MuiThemeProvider } from '@material-ui/core';

import App from './App';
import { updateTokenAction } from './auth/actions';
import { ErrorPage } from './components/error/ErrorPage';
import config from './config';
import { initialiseRUM } from './datadog-rum';
import * as serviceWorker from './serviceWorker';
import { store } from './store';
import translations from './translations';

const requiredModules: ModuleName[] = ['workManagement'];
const permittedRoles: UserRoleName[] = ['administrator', 'safetyManager'];

const hashFragment = window.location.hash.substring(1);

if (hashFragment) {
  const hashParams = new URLSearchParams(hashFragment);
  const hashIdToken = hashParams.get('id_token');
  const hashAccessToken = hashParams.get('access_token');
  const hashRefreshToken = hashParams.get('refresh_token');
  if (hashIdToken && hashAccessToken && hashRefreshToken) {
    localStorage.setItem('id_token', hashIdToken);
    localStorage.setItem('access_token', hashAccessToken);
    localStorage.setItem('refresh_token', hashRefreshToken);
    sessionStorage.setItem('id_token', JSON.stringify(hashIdToken));
    sessionStorage.setItem('access_token', JSON.stringify(hashAccessToken));
    sessionStorage.setItem('refresh_token', JSON.stringify(hashRefreshToken));
    window.history.replaceState({}, '', '/work-management');
  }
}

let authProvider: AuthProvider;

if (hasAuthGatewayOidcTokens()) {
  authProvider = authFactory('OIDC');
} else if (hasCognitoTokens()) {
  authProvider = authFactory('COGNITO');
} else {
  authProvider = authFactory(config.auth.authProvider as AuthProviderName);
}

async function start() {
  await authProvider.login();
  initialiseRUM();

  store.dispatch(updateTokenAction(
    authProvider.logout,
    authProvider.getToken
  ));

  ReactDOM.render((
    <Provider store={store}>
      <MuiThemeProvider theme={checkitTheme}>
        <MicroApp
          authProvider={authProvider}
          requiredModules={requiredModules}
          permittedRoles={permittedRoles}
          translationMessages={translations}
          CustomErrorPage={ErrorPage}
        >
          <App />
        </MicroApp>
      </MuiThemeProvider>
    </Provider>
  ),
    document.getElementById('root')
  );
}

start();

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
